import { useShallow } from 'zustand/react/shallow';
import { useQuery } from '@tanstack/react-query';
import { usePrivy } from '@privy-io/react-auth';

import { DiscountStatus } from '@/types';
import { getPrivyPrimaryAddress } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';
import {
  Discount,
  DiscountGroup,
  isDiscountActive,
} from '@/store/use-capital-store';
import { privateV2Api } from '@/lib/api-clients/rest-client';
import {
  CapitalPlan,
  useCapitalPlans,
} from '@/features/vest-capital/api/get-capital-plans';

interface PromocodeDTO {
  id: string;
  customer_id: string;
  name: string;
  percent_off: number;
  max_redemptions: number;
  times_redeemed: number;
  valid: boolean;
  expires_at: string;
  redeemed_at?: string;
  applicable_products: {
    name: string;
    price_id: string;
  }[];
}

const getPromocodeStatus = (promocode: PromocodeDTO): DiscountStatus => {
  return promocode.valid &&
    (promocode.times_redeemed < promocode.max_redemptions ||
      promocode.max_redemptions === 0)
    ? DiscountStatus.ACTIVE
    : DiscountStatus.REVOKED;
};

const parsePromocodeDTOToDiscountGroup = (
  promocode: PromocodeDTO,
  capitalPlans: CapitalPlan[],
): DiscountGroup => {
  const hasPlans = capitalPlans.length > 0;

  const discounts = (promocode.applicable_products ?? [])
    .map((product) => {
      const capitalPlan = capitalPlans?.find(
        (plan) => plan.priceId === product.price_id,
      );
      const name = capitalPlan?.name ?? product.name;
      const planTitle = name.split('|')[1]?.trim() || name || '';

      return {
        codeId: promocode.id,
        code: promocode.name,
        name,
        planTitle,
        priceId: product.price_id,
        productId: capitalPlan?.productId,
        unitAmount: capitalPlan?.unitAmount,
        discountPct: promocode.percent_off,
        redeemedAt: promocode.redeemed_at,
        expiresAt: promocode.expires_at || '9999-12-31T23:59:59.999Z',
        status: getPromocodeStatus(promocode),
        _hasMatchingPlan: !!capitalPlan,
      };
    })
    .filter(
      (d) => isDiscountActive(d) && (!hasPlans || d._hasMatchingPlan),
    )
    .map(({ _hasMatchingPlan: _, ...discount }) => discount as Discount)
    .sort(
      (a, b) =>
        (a.unitAmount ?? Number.POSITIVE_INFINITY) -
        (b.unitAmount ?? Number.POSITIVE_INFINITY),
    );

  const planTitles = Array.from(
    new Set(discounts.map((discount) => discount.planTitle).filter(Boolean)),
  );

  return {
    codeId: promocode.id,
    code: promocode.name,
    discountPct: promocode.percent_off,
    redeemedAt: promocode.redeemed_at,
    expiresAt: promocode.expires_at || '9999-12-31T23:59:59.999Z',
    status: getPromocodeStatus(promocode),
    discounts,
    planTitles,
  };
};

export async function getPromocodeInfo(
  apiKey: string,
  code: string,
  capitalPlans: CapitalPlan[],
) {
  return privateV2Api
    .post<PromocodeDTO>(
      '/v2/referrals/promo/info',
      { code },
      {
        headers: {
          'X-API-KEY': apiKey,
        },
      },
    )
    .then((response) =>
      parsePromocodeDTOToDiscountGroup(response, capitalPlans),
    );
}

const usePromocodeInfo = (code: string) => {
  const { user } = usePrivy();
  const address = getPrivyPrimaryAddress(user);
  const { data: capitalPlans } = useCapitalPlans({
    queryConfig: {
      enabled: !!address,
    },
  });
  const { hasAuth, apiKey } = useAccountStore(
    useShallow((state) => ({
      hasAuth: state.hasAuth,
      apiKey: state.getAuthParams(address, { skipFallback: true })?.apiKey,
    })),
  );

  return useQuery({
    enabled: hasAuth && !!address && !!apiKey && !!code && !!capitalPlans,
    queryKey: ['promocode-info', address, apiKey, code],
    queryFn: () => getPromocodeInfo(apiKey!, code!, capitalPlans!),
  });
};

export default usePromocodeInfo;
